<template>
  <div class="container">
    <!-- Login Form -->
    <img :src="logoSrc" alt="Logo" height="300" class="h-[200px] mb-14 mr-2 backlight"/>
    <div class="logo-title roboto">Shops Info Board</div>
    <Card>
      <template #content>
        <form @submit.prevent="handleLogin">
          <!-- Shop Selection - Always visible -->
          <div class="mb-4">
            <FloatLabel variant="on">
              <Dropdown
                  id="shop"
                  v-model="selectedShop"
                  :options="availableShops"
                  optionLabel="name"
                  optionValue="id"
                  required
                  class="w-full"
                  placeholder="Select Shop"
                  :loading="isLoadingShops"
              />
            </FloatLabel>
          </div>

          <div class="mb-4">
            <FloatLabel variant="on">
              <InputText id="username" v-model="username" required class="w-full"/>
              <label for="username">Email</label>
            </FloatLabel>
          </div>
          <div class="mb-4">
            <FloatLabel variant="on">
              <Password id="password" v-model="password" required class="w-full" :toggleMask="true" :feedback="false"/>
              <label for="password">Password</label>
            </FloatLabel>
          </div>

          <div class="flex flex-column align-items-center justify-content-center">
            <Button
                label="Login"
                type="submit"
                class="submit-button mt-3"
                :loading="isLoading"
                severity="primary600"
            />
          </div>
        </form>
        <div class="flex flex-col items-center justify-content-center">
          <Button :icon="currentTheme === 'dark' ? 'pi pi-sun' : 'pi pi-moon'" class="mt-4 mr-2 p-button-lg" severity="secondary" text @click="toggleTheme"/>
        </div>
      </template>
    </Card>

    <Toast/>
    <div class="footer-text mt-3">&copy; FoxSolution SRL</div>
  </div>
</template>

<script>
import {ref, onMounted, computed} from 'vue';
import {useRouter} from 'vue-router';
import {useStore} from 'vuex';

import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import Card from 'primevue/card';
import FloatLabel from 'primevue/floatlabel';
import Dropdown from 'primevue/dropdown';

import {useToastService} from '@/utils/toast';
import {useActionToasts} from '@/utils/toastMessages';
import api from '@/utils/axios';

export default {
  components: {Button, InputText, Password, Card, FloatLabel, Dropdown},
  setup() {
    const toast = useToastService();
    const store = useStore();
    const router = useRouter();
    const username = ref('');
    const password = ref('');
    const selectedShop = ref(null);
    const availableShops = ref([]);
    const isLoading = ref(false);
    const isLoadingShops = ref(false);

    const actionToasts = useActionToasts();

    const loginAttempts = ref(0);
    const lastAttemptTime = ref(null);
    const isLocked = ref(false);

    const checkRateLimit = () => {
      const now = Date.now();
      // Reset attempts if 15 minutes have passed
      if (lastAttemptTime.value && (now - lastAttemptTime.value) > 15 * 60 * 1000) {
        loginAttempts.value = 0;
        isLocked.value = false;
      }

      if (isLocked.value) {
        const remainingTime = Math.ceil((15 * 60 * 1000 - (now - lastAttemptTime.value)) / 60000);
        toast.showError(`Too many login attempts. Please try again in ${remainingTime} minutes.`);
        return false;
      }

      return true;
    };

    const loadAllShops = async () => {
      try {
        isLoadingShops.value = true;

        const response = await api.post('/users/login', {
          action: 'getAllShops'
        });

        const data = response.data;

        if (data.success) {
          availableShops.value = data.shops;
        } else {
          toast.showError(data.message || 'Failed to load shops');
        }
      } catch (error) {
        if (error.code === 'ERR_NETWORK') {
          toast.showError('Server connection failed. Please check your network connection.');
        } else {
          toast.showError(error.response?.data?.message || 'Failed to load shops. Please try again later.');
        }
      } finally {
        isLoadingShops.value = false;
      }
    };

    const handleLogin = async () => {
      if (!checkRateLimit()) return;

      if (!selectedShop.value) {
        toast.showError('Please select a shop.');
        return;
      }

      try {
        isLoading.value = true;
        loginAttempts.value++;
        lastAttemptTime.value = Date.now();

        const response = await api.post('/users/login', {
          email: username.value,
          pass: password.value,
          shopId: selectedShop.value
        });

        const data = response.data;

        if (data.success) {
          // Reset attempts on successful login
          loginAttempts.value = 0;
          isLocked.value = false;

          await store.dispatch('users/loginUser', data);

          actionToasts.loginSuccess();

          // Проверяем, есть ли сохраненный путь для восстановления
          const savedPath = localStorage.getItem('redirectAfterLogin');
          if (savedPath) {
            localStorage.removeItem('redirectAfterLogin');

            router.push(savedPath);
          } else {
            router.push('/dashboard');
          }
        } else {
          // Handle rate limit response from backend
          if (data.type === 'rate_limit') {
            isLocked.value = true;
            toast.showError(data.message);
            return;
          }

          const errorMessage = data.message || 'Login failed';
          toast.showError(errorMessage);
        }
      } catch (error) {
        // Enhanced error handling
        if (error.code === 'ERR_NETWORK') {
          toast.showError('Server connection failed. Please check your network connection.');
        } else {
          toast.showError(error.response?.data?.message || 'Login failed. Please try again later.');
        }
      } finally {
        isLoading.value = false;
      }
    };

    const currentTheme = computed(() => store.state.isTheme);

    const toggleTheme = () => {
      const currentTheme = document.documentElement.classList.contains("dark") ? "dark" : "light";
      const newTheme = currentTheme === "dark" ? "light" : "dark";
      document.documentElement.classList.toggle("dark");
      store.dispatch('changeTheme', newTheme);
    };

    const logoSrc = computed(() => {
      return store.state.isTheme === 'dark' ? require('@/assets/ShopInfoboardIcon512.svg') : require('@/assets/ShopInfoboardIcon512.svg');
    });

    onMounted(() => {
      // Load shops on component mount
      loadAllShops();

      store.dispatch('initializeTheme').then(() => {
        const isTheme = localStorage.getItem('isTheme');
        if (isTheme === 'dark') {
          document.documentElement.classList.add("dark");
        } else {
          document.documentElement.classList.remove("dark");
        }
      });
    });

    return {
      username,
      password,
      selectedShop,
      availableShops,
      isLoading,
      isLoadingShops,
      handleLogin,
      currentTheme,
      toggleTheme,
      logoSrc
    };
  },
};
</script>

<style>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  padding-top: 0;
  min-height: calc(100vh - 70px);
}

:deep(.p-card) {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

:deep(.p-card-content) {
  padding: 1.5rem;
}

:deep(.p-password) {
  width: 100%;
}

:deep(.p-password input) {
  width: 100%;
}

:deep(.p-dropdown) {
  width: 100%;
}

.logo-title {
  margin-top: -3rem;
  margin-bottom: 1rem;
  font-size: 2rem;
  font-weight: 600;
  color: var(--text-color);
}

.login-form {
  width: 100%;
  max-width: 400px;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background-color: #fff;
}

.form-group {
  margin-bottom: 20px;
}

.submit-button {
  width: 100%;
}

.footer-text {
  color: var(--p-gray-400);
}

.dark .footer-text {
  color: var(--p-gray-500);
}
</style>
